/**
 * Extra delegated Microsoft Graph permissions that a signed-in person approves
 * on first use. Approval is a fixed, named permission set: callers never pass
 * arbitrary scopes into a consent prompt or a top-level handoff URL.
 */

/** Delegated scopes for the signed-in person's own mailbox. */
export const PERSONAL_MAIL_SCOPES = ['Mail.ReadWrite', 'Mail.Send'] as const;
/** Delegated scopes for shared mailboxes; Exchange still decides which mailboxes the person may open or send as. */
export const SHARED_MAILBOX_SCOPES = ['Mail.ReadWrite.Shared', 'Mail.Send.Shared'] as const;

export const MICROSOFT_CONSENT_KINDS = ['personalMail', 'sharedMailbox'] as const;
export type MicrosoftConsentKind = (typeof MICROSOFT_CONSENT_KINDS)[number];

export const MICROSOFT_CONSENT_SCOPES: Readonly<Record<MicrosoftConsentKind, readonly string[]>> = {
  personalMail: PERSONAL_MAIL_SCOPES,
  sharedMailbox: SHARED_MAILBOX_SCOPES
};

/**
 * Outcome of an approval request:
 * - granted: the permission is now usable here.
 * - pending: approval continues in another tab or a full-page redirect.
 * - cancelled: the person closed the Microsoft window.
 * - blocked: the browser blocked the Microsoft window.
 * - busy: another Microsoft sign-in is already in progress.
 * - failed: Microsoft did not complete the approval.
 */
export type MicrosoftConsentResult = 'granted' | 'pending' | 'cancelled' | 'blocked' | 'busy' | 'failed';

export function isMicrosoftConsentKind(value: unknown): value is MicrosoftConsentKind {
  return typeof value === 'string' && (MICROSOFT_CONSENT_KINDS as readonly string[]).includes(value);
}

/** The named permission set that exactly matches `scopes`, if any. */
export function consentKindForScopes(scopes: readonly string[]): MicrosoftConsentKind | null {
  const requested = new Set(scopes.map((scope) => scope.toLowerCase()));
  for (const kind of MICROSOFT_CONSENT_KINDS) {
    const known = MICROSOFT_CONSENT_SCOPES[kind];
    if (known.length === requested.size && known.every((scope) => requested.has(scope.toLowerCase()))) return kind;
  }
  return null;
}

/** Raised when the person is signed in but has not yet approved the requested permissions. */
export class MicrosoftConsentRequiredError extends Error {
  readonly kind: MicrosoftConsentKind | null;

  constructor(scopes: readonly string[]) {
    super('Microsoft needs your approval before this app can use this access.');
    this.name = 'MicrosoftConsentRequiredError';
    this.kind = consentKindForScopes(scopes);
  }
}
